/**
 * Mount command (plan Task 7.16, D11 / R6): one tab per operating system with the variants `mountCommandsFor` gives
 * for the share's protocol (NFS: Linux only), their notes, and Copy through the clipboard bridge. The commands carry
 * the `<storage-account-key>` placeholder and never the key or a SAS — nothing here reads the connection's secret.
 * An opener that does not know the protocol lets main resolve it (`azure-storage:share-protocol`, scope-checked).
 */
import { useState } from 'react';
import { Copy } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { resolvedServiceEndpoint, resolveStorageEndpoints } from '../../../../../core/engines/azureStorageEndpointResolution';
import { PROTOCOL_UNKNOWN_REASON } from '../../../../../core/engines/azureStorageFileCoverage';
import { mountCommandsFor } from '../../../../../core/engines/azureStorageMountCommands';
import { writeClipboardText } from '../../../../../lib/clipboard';
import type { FileShareProtocol, MountCommandOs, MountCommandVariant } from '../../../../../types/azureStorage';
import type { Connection } from '../../../../../types/electron';
import type { ShareDialogRequest } from '../fileDialogStore';
import { startShareRequest } from '../shareApi';
import { trackShareAction } from '../shareActions';
import { useShareConnection } from './shareGate';
import { useShareRead } from './useShareRead';

type Props = { request: ShareDialogRequest; onClose: () => void };

export const MOUNT_UNAVAILABLE_REASON = 'Mount commands need the account\'s file endpoint; this connection has none (Azurite has no Files service).';

function variantsFor(connection: Connection | undefined, share: string, protocol: FileShareProtocol): MountCommandVariant[] {
  if (!connection) return [];
  const account = resolveStorageEndpoints(connection);
  const endpoint = resolvedServiceEndpoint(connection, 'file');
  const fileHost = endpoint ? new URL(endpoint).host : undefined;
  if (!account.accountName) return [];
  return mountCommandsFor({ account: account.accountName, fileHost, share, protocol, emulator: account.emulator });
}

export function MountCommandDialog({ request, onClose }: Props) {
  const share = request.resource?.share ?? '';
  const live = { kind: 'share', share } as const;
  const known = request.protocol === 'smb' || request.protocol === 'nfs' ? request.protocol : undefined;
  const connection = useShareConnection(request.connectionId);
  const read = useShareRead({
    connectionId: request.connectionId, resource: live, purpose: 'protocol', enabled: !known,
    start: () => startShareRequest('shareProtocol', { connectionId: request.connectionId, resource: live }),
  });
  const resolved = known ?? (read.data?.protocol === 'smb' || read.data?.protocol === 'nfs' ? read.data.protocol : undefined);
  const variants = resolved ? variantsFor(connection, share, resolved) : [];
  const [os, setOs] = useState<MountCommandOs | undefined>();
  const selected = variants.find((variant) => variant.os === os) ?? variants[0];

  const copy = async (variant: MountCommandVariant) => {
    const copied = await writeClipboardText(variant.command);
    trackShareAction('mount_command_copy', copied ? 'ok' : 'error', { source: request.source, protocol: variant.protocol }, { os: variant.os });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="mount-command-dialog">
        <DialogHeader>
          <DialogTitle>Mount {share}</DialogTitle>
          <DialogDescription>Commands that mount this share on another machine. Jam SQL never puts the account key in them.</DialogDescription>
        </DialogHeader>
        {!resolved && read.isLoading && <p className="text-sm text-muted-foreground" data-testid="mount-command-loading">Reading the share's protocol…</p>}
        {!resolved && !read.isLoading && <p className="text-sm text-muted-foreground" data-testid="mount-command-protocol-unknown">{read.error?.message ?? PROTOCOL_UNKNOWN_REASON}</p>}
        {resolved && variants.length === 0 && <p className="text-sm text-muted-foreground" data-testid="mount-command-unavailable">{MOUNT_UNAVAILABLE_REASON}</p>}
        {selected && (
          // UI review F5: `min-w-0` lets the dialog's grid column stay at the dialog's width, so a long command scrolls
          // inside its own box and the Copy button — beside the box, never over its scrolled text — stays visible.
          <Tabs className="min-w-0" value={selected.os} onValueChange={(next) => setOs(next as MountCommandOs)}>
            <TabsList>
              {variants.map((variant) => <TabsTrigger key={variant.os} value={variant.os} data-testid={`mount-command-os-${variant.os}`}>{variant.label}</TabsTrigger>)}
            </TabsList>
            {variants.map((variant) => (
              <TabsContent key={variant.os} value={variant.os} className="min-w-0 space-y-3 pt-3">
                <div className="flex min-w-0 items-start gap-2">
                  <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded border border-border bg-muted/40 p-3 font-mono text-xs" tabIndex={0} data-testid="mount-command-text">{variant.command}</pre>
                  <Button type="button" size="xs" variant="outline" className="shrink-0 gap-1" onClick={() => void copy(variant)} data-testid="mount-command-copy">
                    <Copy className="h-3.5 w-3.5" />Copy
                  </Button>
                </div>
                <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground" data-testid="mount-command-notes">
                  {variant.notes.map((note) => <li key={note}>{note}</li>)}
                </ul>
              </TabsContent>
            ))}
          </Tabs>
        )}
        {/* U17: a visible way out, as every other storage dialog has. */}
        <DialogFooter><Button type="button" variant="outline" onClick={onClose} data-testid="mount-command-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
